import { appName } from "@getdevpulse/settings/browser"
import { NavbarDropdownStore, UIShell } from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { MobxRouter, startRouter } from "mobx-router"
import { useEffect, useMemo } from "react"
import { logger } from "./lib/logger"
import { routes } from "./routes/routes"
import {
  StoreProvider,
  useAuthStore,
  useI18nStore,
  useMetadataStore,
  useStore,
  useThemeStore,
  useViewStore,
} from "./stores/StoreContext"
import "./index.css"

const AppContent = observer(() => {
  const store = useStore()
  const authStore = useAuthStore()
  const viewStore = useViewStore()
  const metadataStore = useMetadataStore()
  const themeStore = useThemeStore()
  const i18nStore = useI18nStore()

  // Check if teams are available
  const hasTeams = metadataStore.uniqueTeams.length > 0

  // Get current route path for active menu highlighting
  const currentPath = store.router.currentPath || window.location.pathname

  // Create navbar dropdown store
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale triggers re-render
  const navbarDropdown = useMemo(() => {
    return NavbarDropdownStore.create({
      placeholder: i18nStore.t("viewSelectPlaceholder"),
      selectedOption: viewStore.currentView,
      options: [
        {
          id: "organization",
          label: i18nStore.t("viewOrganization"),
          disabled: false,
        },
        {
          id: "group",
          label: i18nStore.t("viewGroup"),
          disabled: !hasTeams,
        },
        {
          id: "individual",
          label: i18nStore.t("viewIndividual"),
          disabled: false,
        },
      ],
    })
  }, [viewStore.currentView, hasTeams, i18nStore.locale])

  // Create menu items - Teams menu is only visible if teams are available
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nStore.locale triggers re-render
  const menuItems = useMemo(() => {
    const items = [
      {
        label: i18nStore.t("navDashboard"),
        href: "/",
        onClick: () => store.router.goTo(routes.home),
        active: currentPath === "/",
      },
      {
        label: i18nStore.t("navContributors"),
        href: "/contributors",
        onClick: () => store.router.goTo(routes.contributors),
        active: currentPath === "/contributors",
      },
    ]

    // Add Teams menu item only if teams are available
    if (hasTeams) {
      items.push({
        label: i18nStore.t("navTeams"),
        href: "/teams",
        onClick: () => store.router.goTo(routes.teams),
        active: currentPath === "/teams",
      })
    }

    // Add Repositories menu item
    items.push({
      label: i18nStore.t("navRepositories"),
      href: "/repositories",
      onClick: () => store.router.goTo(routes.repositories),
      active: currentPath === "/repositories",
    })

    // Add DORA Metrics menu item
    items.push({
      label: i18nStore.t("doraMetricsTitle"),
      href: "/dora-metrics",
      onClick: () => store.router.goTo(routes.doraMetrics),
      active: currentPath === "/dora-metrics",
    })

    return items
  }, [hasTeams, store.router, currentPath, i18nStore.locale])

  // Handle dropdown selection changes
  useEffect(() => {
    if (navbarDropdown.selectedOption !== viewStore.currentView) {
      navbarDropdown.setSelectedOption(viewStore.currentView)
    }
  }, [viewStore.currentView, navbarDropdown])

  useEffect(() => {
    // Start the router
    startRouter(routes, store, {
      html5history: true,
      notfound: () => {
        store.router.goTo(routes.notFound)
      },
    })
  }, [store])

  // Check if auth is enabled
  const isAuthEnabled = authStore.userManager !== null

  // Load data when appropriate
  useEffect(() => {
    const shouldLoadData =
      // If no auth, load immediately
      (!isAuthEnabled && !store.isDataReady) ||
      // If auth enabled and user is authenticated, load data
      (isAuthEnabled && authStore.isAuthenticated && !store.isDataReady)

    if (shouldLoadData) {
      logger.info("Loading data...", {
        component: "App",
        authEnabled: isAuthEnabled,
        authenticated: authStore.isAuthenticated,
      })
      store.initializeData().catch((error) => {
        logger.error("Failed to load data", error, { component: "App" })
      })
    }
  }, [store, isAuthEnabled, authStore.isAuthenticated])

  // If auth is enabled and user is not authenticated, redirect to login
  useEffect(() => {
    if (isAuthEnabled && !authStore.isAuthenticated && !authStore.isLoading) {
      // Check if we're on a callback route (don't redirect during auth flow)
      const isCallbackRoute =
        window.location.pathname.includes("/callback") ||
        window.location.pathname.includes("/auth/github/callback")

      if (!isCallbackRoute) {
        logger.debug("User not authenticated, initiating login", {
          component: "App",
        })
        authStore.login()
      }
    }
  }, [isAuthEnabled, authStore.isAuthenticated, authStore.isLoading, authStore])

  const handleLogout = () => {
    logger.track("logout_button_clicked", { location: "UIShell" })
    authStore.logout()
  }

  // If auth is disabled, always show UI Shell
  if (!isAuthEnabled) {
    // Show loading state while data is initializing
    if (store.data.isLoading || store.metadata.isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" />
            <p className="text-lg">{i18nStore.t("loading")}</p>
          </div>
        </div>
      )
    }

    // Show error if data loading failed
    if (store.data.loadError || store.metadata.loadError) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold text-red-600 mb-4">{i18nStore.t("loadingError")}</h1>
            <p className="text-muted-foreground mb-4">
              {store.data.loadError || store.metadata.loadError}
            </p>
            <button
              type="button"
              onClick={() => store.initializeData()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              {i18nStore.t("retry")}
            </button>
          </div>
        </div>
      )
    }

    const logo = `/logo.png`

    return (
      <UIShell
        appIcon={{
          image: logo,
          label: appName,
        }}
        logo={{
          image: logo,
          label: appName,
          onClick: () => store.router.goTo(routes.home),
        }}
        appName={appName}
        menuItems={menuItems}
        navbarDropdown={{
          store: navbarDropdown,
          onSelect: (optionId: string) => {
            viewStore.setView(optionId as "organization" | "group" | "individual")
          },
        }}
        languageSelector={{
          currentLanguage: i18nStore.locale,
          languages: [
            { code: "en", label: i18nStore.t("languageEnglish") },
            { code: "de", label: i18nStore.t("languageGerman") },
            { code: "es", label: i18nStore.t("languageSpanish") },
            { code: "zh", label: i18nStore.t("languageChinese") },
          ],
          onLanguageChange: (code) => i18nStore.setLocale(code as "en" | "de" | "es" | "zh"),
        }}
        onThemeToggle={() => themeStore.toggleTheme()}
      >
        <MobxRouter store={store} />
      </UIShell>
    )
  }

  // If auth is enabled, show loading or authenticated content
  if (authStore.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" />
          <p className="text-lg">{i18nStore.t("authenticating")}</p>
        </div>
      </div>
    )
  }

  // Show loading state while data is initializing (after auth)
  if (authStore.isAuthenticated && (store.data.isLoading || store.metadata.isLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto mb-4" />
          <p className="text-lg">{i18nStore.t("loading")}</p>
        </div>
      </div>
    )
  }

  // Show error if data loading failed (after auth)
  if (authStore.isAuthenticated && (store.data.loadError || store.metadata.loadError)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{i18nStore.t("loadingError")}</h1>
          <p className="text-muted-foreground mb-4">
            {store.data.loadError || store.metadata.loadError}
          </p>
          <button
            type="button"
            onClick={() => store.initializeData()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            {i18nStore.t("retry")}
          </button>
        </div>
      </div>
    )
  }

  // Show error if authentication failed
  if (authStore.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{i18nStore.t("authError")}</h1>
          <p className="text-muted-foreground mb-4">{authStore.error}</p>
          <button
            type="button"
            onClick={() => authStore.login()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            {i18nStore.t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  // User is authenticated - show UI Shell with user info
  return (
    <UIShell
      appIcon={{
        image: "/logo.png",
        label: appName,
      }}
      logo={{
        image: "/logo.png",
        label: appName,
        onClick: () => store.router.goTo(routes.home),
      }}
      appName={appName}
      menuItems={menuItems}
      navbarDropdown={{
        store: navbarDropdown,
        onSelect: (optionId: string) => {
          viewStore.setView(optionId as "organization" | "group" | "individual")
        },
      }}
      languageSelector={{
        currentLanguage: i18nStore.locale,
        languages: [
          { code: "en", label: i18nStore.t("languageEnglish") },
          { code: "de", label: i18nStore.t("languageGerman") },
          { code: "es", label: i18nStore.t("languageSpanish") },
          { code: "zh", label: i18nStore.t("languageChinese") },
        ],
        onLanguageChange: (code) => i18nStore.setLocale(code as "en" | "de" | "es" | "zh"),
      }}
      onThemeToggle={() => themeStore.toggleTheme()}
      rightIcons={
        authStore.user
          ? [
              {
                icon: (
                  <div className="flex items-center gap-2">
                    <span className="text-sm hidden md:block">
                      {authStore.user.profile.name || authStore.user.profile.email || "User"}
                    </span>
                  </div>
                ),
                children: [
                  {
                    label: i18nStore.t("profile"),
                    onClick: () => logger.info("Profile clicked", { component: "App" }),
                  },
                  {
                    label: i18nStore.t("settings"),
                    onClick: () => logger.info("Settings clicked", { component: "App" }),
                  },
                  {
                    label: i18nStore.t("logout"),
                    onClick: handleLogout,
                  },
                ],
              },
            ]
          : undefined
      }
    >
      <MobxRouter store={store} />
    </UIShell>
  )
})

function App() {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  )
}

export default App
